import styles from './login-form.module.css'

import React from 'react'
import type { Location } from 'react-router-dom'

import api from '@disco/api/server'

import LoginForm from 'components/common/login-form'
import Separator from 'components/common/login-form/separator'
import { Box, Button, Flex, Heading, Text } from 'components/ds'
import DiscoIcon from 'components/ui/icon/disco-icon'

import type { AddToDiscoUser } from '../types'
import { useTranslation } from '@disco/i18n/use-translation'

type Props = {
  user: AddToDiscoUser
  showSignIn: boolean
  onShowSignIn: () => void
  onLoginUser: (...args: Array<any>) => any
  onForgotPassword: (email: string, returnUrl: string) => Promise<any>
  onGetSignUpUrl: () => Promise<string>
}

const AddToDiscoLoginForm = ({
  user,
  showSignIn,
  onShowSignIn,
  onLoginUser,
  onForgotPassword,
  onGetSignUpUrl,
}: Props): React.ReactNode => {
  const { t } = useTranslation('shares')
  const handleGetStarted = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const signupUrl = await onGetSignUpUrl()
      const url = new URL(signupUrl)
      url.searchParams.set('utm_source', 'save_to_disco_modal_gateway')
      window.open(url.toString(), '_blank', 'noopener,noreferrer')
    } catch {
      // signup URL fetch failed — do nothing
    }
  }

  if (showSignIn) {
    return (
      <Box width="312px">
        <LoginForm
          user={user}
          location={window.location as unknown as Location}
          heading={t('shares:signIn', 'Sign in')}
          message={t(
            'shares:contentWillBeAdded',
            'This content will be added to your DISCO',
          )}
          onLoginUser={onLoginUser}
          onForgotPassword={onForgotPassword}
          onSamlDiscovery={async email =>
            api.samlLogin({
              username: email.trim(),
              redirect_uri: window.location.href,
            })
          }
          onGetSignupUrl={onGetSignUpUrl}
        />
      </Box>
    )
  }

  return (
    <Flex direction="column" gap="3" width="312px">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="4">
          <Heading size="6" m="0">
            {t('shares:saveToDisco', 'Save to DISCO')}
          </Heading>
          <Text size="2" color="gray">
            {t(
              'shares:savedAsNewPlaylist',
              'These tracks will be saved as a new playlist in your DISCO account.',
            )}
          </Text>
        </Flex>
        <form onSubmit={handleGetStarted}>
          <Button
            type="submit"
            size="3"
            color="gray"
            highContrast
            radius="medium"
            className={styles.FullWidthButton}
            data-testid="signup-get-started-button"
          >
            <DiscoIcon />
            {t('shares:signUpToDisco', 'Sign up to DISCO')}
          </Button>
        </form>
      </Flex>

      <Separator or />

      <Flex direction="column" gap="3" align="center">
        <Button
          type="button"
          size="3"
          variant="soft"
          color="gray"
          className={styles.FullWidthButton}
          onClick={onShowSignIn}
        >
          {t('shares:signIn', 'Sign in')}
        </Button>
      </Flex>
    </Flex>
  )
}

export default AddToDiscoLoginForm
